<script lang="ts" setup>
import { getCmsImage } from '#imports';
import { ChevronRight } from 'lucide-vue-next';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { useMediaQuery } from '@vueuse/core';

// Define proper TypeScript interfaces
interface CallToAction {
  label?: string;
  url?: string;
  element?: {
    slug: string;
  };
}

interface FeatureImage {
  title: string;
  alt?: string;
}

interface Feature {
  heading: string;
  label?: string;
  body?: { html: string };
  images?: FeatureImage[];
  callToAction?: CallToAction;
}

interface Props {
  title?: string;
  subheading?: string;
  features: Feature[];
  cardsSlider?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  features: () => [],
  cardsSlider: false,
});

const { locale, t } = useI18n();

const isMobile = useMediaQuery('(max-width: 767px)');

const showSlider = computed(() => props.cardsSlider && isMobile.value);

// Compute the number of features
const featureCount = computed(() => {
  return props.features?.length || 0;
});

const count = computed(() => featureCount.value);

// Compute grid classes and gap based on the number of features
const gridClasses = computed(() => {
  if (count.value === 0) return 'grid-cols-1';
  if (count.value === 1) return 'grid-cols-1';
  if (count.value === 2) return 'grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12';
  if (count.value === 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8';

  // For 4 or more features, use responsive layout up to 4 columns
  return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6';
});

// Compute content alignment based on feature count
const contentAlignment = computed(() => {
  return featureCount.value === 2 ? 'items-start justify-center text-center' : 'items-end justify-start';
});


</script>

<template>
  <div class="py-8">
    <VContainer>
      <div v-if="subheading" class="mb-6">
        <div class="inline text-sm lg:text-sm font-bold uppercase tracking-wider py-2 px-4 bg-primary-50 rounded">
          {{ subheading }}
        </div>
      </div>

      <div v-if="title">
        <h2 class="text-[2.25rem] mb-6 font-bold text-gray-900">
          {{ title }}
        </h2>
      </div>

      <ClientOnly v-if="showSlider && features.length > 0">
        <Swiper
          :space-between="20"
          :slides-per-view="1"
          :navigation="false"
          :pagination="features.length > 1 ? { clickable: true } : false"
          :autoplay="features.length > 1 ? { delay: 4000, disableOnInteraction: false } : false"
          :modules="[Navigation, Pagination, Autoplay]"
          class="pb-10!"
        >
        <SwiperSlide v-for="(feature, index) in features" :key="`feature-slide-${index}`">
          <article class="group relative">
            <div v-if="feature.images?.length" class="relative h-full rounded-lg overflow-hidden isolate">
              <NuxtImg
                :src="getCmsImage(feature.images)"
                :alt="feature.images[0]?.alt || feature.images[0]?.title || feature.heading"
                :title="feature.images[0]?.title || feature.heading"
                format="webp"
                loading="lazy"
              class="w-full lg:min-h-96 lg:aspect-square shadow-lg object-cover transition-transform duration-300 group-hover:scale-105 aspect-video"
            />

              <div
                class="absolute inset-0 bg-linear-to-t from-black/50 to-black/30 flex transition-opacity duration-300 group-hover:from-black/80 items-end justify-start"
              >
                <div class="flex flex-col gap-3 p-10 text-white max-w-full">
                  <h3 class="font-bold leading-tight text-[1.5rem]">
                    {{ feature.heading }}
                  </h3>

                  <div v-if="feature.body" class="text-sm lg:text-lg opacity-90 leading-relaxed" v-html="feature.body?.html"></div>

                  <div v-if="feature.callToAction">
                    <NuxtLink
                      v-if="feature.callToAction.element?.slug"
                      :to="`/${locale}/${feature.callToAction.element.slug}`"
                      class="inline-flex items-center gap-2 py-2 text-base font-medium transition-colors duration-200 hover:text-primary-200"
                      :aria-label="`${feature.callToAction.label || t('Learn More')} about ${feature.heading}`"
                    >
                      <span class="underline underline-offset-4">
                        {{ feature.callToAction.label || t('Learn More') }}
                      </span>
                      <ChevronRight class="size-4 lg:size-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </NuxtLink>

                    <NuxtLink
                      v-else-if="feature.callToAction.url"
                      :to="feature.callToAction.url"
                      class="inline-flex items-center gap-2 py-2 text-base font-medium transition-colors duration-200 hover:text-primary-200"
                      :aria-label="`${feature.callToAction.label || t('Learn More')} about ${feature.heading}`"
                    >
                      <span class="underline underline-offset-4">
                        {{ feature.callToAction.label || t('Learn More') }}
                      </span>
                      <ChevronRight class="size-4 lg:size-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </SwiperSlide>
      </Swiper>
      </ClientOnly>

      <div v-if="!showSlider && features.length > 0" class="grid" :class="gridClasses">
        <article v-for="(feature, index) in features" :key="`feature-${index}`" class="group relative">
          <div v-if="feature.images?.length" class="relative h-full rounded-lg overflow-hidden isolate">
            <NuxtImg
              :src="getCmsImage(feature.images)"
              :alt="feature.images[0]?.alt || feature.images[0]?.title || feature.heading"
              :title="feature.images[0]?.title || feature.heading"
              format="webp"
              loading="lazy"
              :class="[count == 2 ? 'aspect-auto min-h-96': 'aspect-video']"
              class="w-full lg:min-h-96 lg:aspect-square shadow-lg object-cover transition-transform duration-300 group-hover:scale-105"
            />

            <!-- Content overlay -->
            <div
              class="absolute inset-0 bg-linear-to-t from-black/50 to-black/30 flex transition-opacity duration-300 group-hover:from-black/80"
              :class="contentAlignment"
            >
              <div class="flex flex-col gap-3 p-10 text-white max-w-full">
                <!-- Label -->
                <span v-if="feature.label && count == 2" class="text-xl font-bold uppercase tracking-wider opacity-90">
                  {{ feature.label }}
                </span>

                <!-- Heading -->
                <h3 class="font-bold leading-tight text-[1.5rem]">
                  {{ feature.heading }}
                </h3>

                <!-- Body text -->
                <div v-if="feature.body && count > 1 && count < 4" class="text-sm lg:text-lg opacity-90 leading-relaxed" v-html="feature.body?.html"></div>

                <!-- Call to action -->
                <div v-if="feature.callToAction">
                  <NuxtLink
                    v-if="feature.callToAction.element?.slug"
                    :to="`/${locale}/${feature.callToAction.element.slug}`"
                    class="inline-flex items-center gap-2 py-2 text-base font-medium transition-colors duration-200 hover:text-primary-200"
                    :aria-label="`${feature.callToAction.label || t('Learn More')} about ${feature.heading}`"
                  >
                    <span class="underline underline-offset-4">
                      {{ feature.callToAction.label || t('Learn More') }}
                    </span>
                    <ChevronRight class="size-4 lg:size-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </NuxtLink>

                  <NuxtLink
                    v-else-if="feature.callToAction.url"
                    :to="feature.callToAction.url"
                    class="inline-flex items-center gap-2 py-2 text-base font-medium transition-colors duration-200 hover:text-primary-200"
                    :aria-label="`${feature.callToAction.label || t('Learn More')} about ${feature.heading}`"
                  >
                    <span class="underline underline-offset-4">
                      {{ feature.callToAction.label || t('Learn More') }}
                    </span>
                    <ChevronRight class="size-4 lg:size-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </VContainer>
  </div>
</template>
